import {ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {environment} from '../../environments/environment';
import {DataService} from '../services/data.service';
import {TranslationService} from '../services/translation.service';

interface KioskActionResult {
    Success: boolean;
    Message: string;
}

@Component({
    selector: 'app-airport-kiosk',
    templateUrl: './airport-kiosk.component.html',
    styleUrls: ['./airport-kiosk.component.css']
})
export class AirportKioskComponent implements OnInit, OnDestroy {
    apiBaseUrl = `${environment.baseUrl}/soc-admin/airport-kiosk`;
    logsText = 'Loading recent logs...';
    logsMeta = 'Loading...';
    actionMessage = '';
    isLoadingLogs = false;
    isRunningAction = false;

    private refreshTimer: any;
    private actionMessageTimer: any;
    private sessionSubscription: Subscription;

    constructor(private dataService: DataService,
                private ngZone: NgZone,
                private changeDetectorRef: ChangeDetectorRef,
                private router: Router,
                private translationService: TranslationService) {
    }

    ngOnInit(): void {
        this.sessionSubscription = this.dataService.dataChangedSession.subscribe(() => {
            this.startLogRefresh();
        });
        this.startLogRefresh();
    }

    ngOnDestroy(): void {
        this.stopLogRefresh();

        if (this.sessionSubscription) {
            this.sessionSubscription.unsubscribe();
        }

        this.clearActionMessageTimer();
    }

    async loadLogs(): Promise<void> {
        if (!this.dataService.getSessionIdentifier() || this.isLoadingLogs) {
            return;
        }

        this.isLoadingLogs = true;

        try {
            const result = await this.getJson<KioskActionResult>('logs/recent');
            const now = new Date().toLocaleTimeString();

            if (!result.Success) {
                this.logsMeta = `Failed at ${now}`;
                this.logsText = result.Message || 'Could not load recent logs.';
                return;
            }

            this.logsMeta = `Updated ${now}`;
            this.logsText = result.Message || 'No log output.';
        } catch (error) {
            this.logsMeta = 'Failed';
            this.logsText = this.getErrorMessage(error);
        } finally {
            this.isLoadingLogs = false;
        }
    }

    async restartKiosk(): Promise<void> {
        const confirmed = window.confirm(this.translationService.translate(
            'You are about to restart the kiosk. This is a dangerous action. Are you sure you want to continue?'));

        if (!confirmed) {
            return;
        }

        await this.runAction('actions/reboot-kiosk', 'Restart kiosk');
    }

    private async runAction(endpoint: string, label: string): Promise<void> {
        this.isRunningAction = true;
        this.setActionMessage(`${label} started...`);

        try {
            const result = await this.postJson<KioskActionResult>(endpoint);

            if (!result.Success) {
                this.setActionMessage(result.Message || `${label} failed.`, true);
                return;
            }

            this.setActionMessage(result.Message || `${label} completed.`, true);
            await this.loadLogs();
        } catch (error) {
            this.setActionMessage(this.getErrorMessage(error), true);
        } finally {
            this.isRunningAction = false;
        }
    }

    private setActionMessage(message: string, autoClear = false): void {
        this.clearActionMessageTimer();
        this.actionMessage = message;

        if (!autoClear) {
            return;
        }

        this.actionMessageTimer = window.setTimeout(() => {
            this.ngZone.run(() => {
                this.actionMessage = '';
                this.actionMessageTimer = null;
                this.changeDetectorRef.detectChanges();
            });
        }, 5000);
    }

    private clearActionMessageTimer(): void {
        if (this.actionMessageTimer) {
            clearTimeout(this.actionMessageTimer);
            this.actionMessageTimer = null;
        }
    }

    private startLogRefresh(): void {
        if (!this.dataService.getSessionIdentifier() || this.refreshTimer) {
            return;
        }

        this.loadLogs();
        this.refreshTimer = setInterval(() => this.loadLogs(), 3000);
    }

    private stopLogRefresh(): void {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }

    private async getJson<T>(endpoint: string): Promise<T> {
        const response = await fetch(`${this.apiBaseUrl}/${endpoint}`, {
            headers: this.getAuthHeaders()
        });

        return this.parseJsonResponse<T>(response);
    }

    private async postJson<T>(endpoint: string): Promise<T> {
        const response = await fetch(`${this.apiBaseUrl}/${endpoint}`, {
            method: 'POST',
            headers: this.getAuthHeaders()
        });

        return this.parseJsonResponse<T>(response);
    }

    private async parseJsonResponse<T>(response: Response): Promise<T> {
        if (response.status === 401 || response.status === 403) {
            this.stopLogRefresh();
            this.router.navigate(['/login']);
            throw new Error('Your session has expired. Please sign in again.');
        }

        const responseText = await response.text();
        let result: any;

        try {
            result = JSON.parse(responseText);
        } catch {
            throw new Error(responseText || `Airport Kiosk API returned ${response.status}.`);
        }

        if (!response.ok) {
            throw new Error(result.Message || `Airport Kiosk API returned ${response.status}.`);
        }

        return result;
    }

    private getErrorMessage(error: any): string {
        if (error && error.message) {
            return error.message;
        }

        return 'Airport Kiosk request failed.';
    }

    private getAuthHeaders(): HeadersInit {
        return {
            Token: this.dataService.getSessionIdentifier() || ''
        };
    }
}
